import { LocationDTO } from '@nx-smartmonkey/shared/domain';
import { validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import { toSeconds } from '../../../helpers/excel/Common';
import { ColumnDefinition, FieldDefinition } from './FieldDefinitions';

export interface MappingItem {
  column: ColumnDefinition;
  field: FieldDefinition;
  errors: number;
  empty: number;
  valid: number;
}

export class FieldMapping {
  constructor(private items: MappingItem[], private data: any[][]) {}

  getByColumnIndex(columnIndex: number) {
    return this.items.find((item) => item.column.index === columnIndex);
  }
  getById(id: string): MappingItem | undefined {
    return this.items.find((item) => item.field.id === id);
  }
  removeByColumn(columnIndex: number) {
    this.items = this.items.filter((item) => item.column.index !== columnIndex);
    return this.getCopy();
  }
  removeById(id: string) {
    this.items = this.items.filter((item) => item.field.id !== id);
    return this.getCopy();
  }
  getItems() {
    return this.items;
  }

  setAddresses(locations: (LocationDTO | null)[]) {
    if (locations.length !== this.data.length) {
      throw new Error(`The number of addresses does not match the number of rows`);
    }

    this.data.forEach((d: any, index: number) => {
      d[`location`] = locations[index];
    });

    return this.getCopy();
  }

  private getCopy() {
    return new FieldMapping(
      this.items.map((item) => ({ ...item })),
      this.data
    );
  }

  getDataByColumnIndex(columnIndex: number) {
    return this.data.map((row) => row[columnIndex]);
  }

  add(field: FieldDefinition, column: ColumnDefinition) {
    if (this.getById(field.id)) {
      this.removeById(field.id);
    }
    if (this.getByColumnIndex(column.index)) {
      this.removeByColumn(column.index);
    }

    // count the number of empty rows
    const empty = this.data.filter((row) => !row[column.index]).length;
    // count the number of errors
    const errors = this.data.filter((row) => !FieldMapping.validate(field, row[column.index])).length;
    // count the number of valid rows
    const valid = this.data.length - empty - errors;

    this.items.push({
      column,
      field,
      errors,
      empty,
      valid,
    });
    return this.getCopy();
  }

  getData(): Array<{ id: string; [value: string]: any }> {
    return this.data.map((row, idx) => {
      // replace all address fields with a single one
      const data: { id: string; [value: string]: any } = { id: `${idx}` };
      this.items.forEach((item, idx) => {
        // skip all keys starting by address.
        if (item.field.id.startsWith(`address.`)) return;

        const finalValue = FieldMapping.cast(item.field, row[item.column.index]);
        if (!finalValue) return;

        if (item.field.isCustomField) {
          data.custom_fields = data.custom_fields || {};
          data[`custom_fields`][`${item.field.id}`] = FieldMapping.cast(item.field, row[item.column.index]);
        } else {
          data[item.field.id] = FieldMapping.cast(item.field, row[item.column.index]);
        }
      });
      return data;
    });
  }

  static cast(field: FieldDefinition, value: string) {
    if (value === undefined || value === null || value === ``) return null;
    if (field.type === `numerical`) {
      try {
        return parseFloat(value);
      } catch (e) {
        return null;
      }
    }
    if (field.type === `time_minutes`) {
      try {
        const minutes = parseInt(value);
        return minutes * 60;
      } catch (e) {
        return null;
      }
    }
    if (field.type === `text`) {
      return `${value}`;
    }
    if (field.type === `phone`) {
      return this.validatePhone(value);
    }
    if (field.type === `boolean`) {
      return value === `true`;
    }
    if (field.type === `categorical`) {
      if (!field.isMultipleChoice) {
        return value;
      }
      if (field.isMultipleChoice) {
        return value.split(`,`);
      }
    }
    if (field.type === `list`) {
      // split by comma, tab, space or semicolon
      value = `${value}`.replace(/,|;|\t|\s/g, `,`);
      if (field.options) {
        return value
          .split(`,`)
          .filter((v) => field.options?.includes(v))
          .map((v) => v.replace(/\s/g, ``));
      }
      return value.split(`,`).map((v) => v.replace(/\s/g, ``));
    }
    if (field.type === `time_window`) {
      // Remove all whitespaces
      value = `${value}`.replace(/\s/g, ``);

      // Split by comma, tab, space or semicolon
      value = value.replace(/,|;|\t|\s/g, `,`);

      const splitted = value.split(`,`).filter((part) => part !== ``);

      const arrayTW: Array<[number, number] | null> = splitted.map((val: any) => {
        try {
          const [sText, eText] = val.split(`-`);
          const timeStart = toSeconds(sText);
          let timeEnd = toSeconds(eText);

          if (timeStart === null || timeEnd === null) {
            return null;
          }

          // Adding 24 hours if timeStart is higher than timeEnd. Each time is from different days (maximum 1 day).
          timeEnd = timeStart > timeEnd ? timeEnd + 3600 * 24 : timeEnd;

          return [timeStart, timeEnd];
        } catch (e) {
          return null;
        }
      });

      // If contains null, there are errors.
      // If field is not multiple but value has more than one time window, it's an error.
      if (arrayTW.includes(null) || (!field.multiple && arrayTW.length > 1)) {
        return null;
      }

      // Sort time windows by start time
      const sortedTW = (arrayTW as Array<[number, number]>).sort((tw1: [number, number], tw2: [number, number]) => {
        const tw1StartTime = tw1[0];
        const tw2StartTime = tw2[0];

        if (tw1StartTime < tw2StartTime) {
          return -1;
        }
        return 1;
      });

      // Check if there are overlapping time windows
      for (let index = 0; index < sortedTW.length; index++) {
        const current = sortedTW[index];
        const next = sortedTW[index + 1];

        if (!next) break;

        if (current[1] >= next[0]) return null;
      }
      return sortedTW;
    }
    return null;
  }

  hasValidAddress(): boolean {
    // There are three possible valid adddresses.
    // * If mapping contains address.lat and address.lng
    // * If mapping contains address.label
    // * If mapping contains address.street and address.number

    const hasLat = this.getById(`address.lat`);
    const hasLng = this.getById(`address.lng`);
    const hasLabel = this.getById(`address.label`);
    const hasStreet = this.getById(`address.street`);
    const hasNumber = this.getById(`address.number`);

    return Boolean((hasLat && hasLng) || hasLabel || (hasStreet && hasNumber));
  }

  hasAnyAddressField(): boolean {
    return this.items.some((item) => FieldMapping.isAddressField(item.field));
  }

  getAddresses(): string[] {
    const hasLat = this.getById(`address.lat`);
    const hasLng = this.getById(`address.lng`);

    const hasLabel = this.getById(`address.label`);
    const hasStreet = this.getById(`address.street`);
    const hasNumber = this.getById(`address.number`);
    const tags = [
      this.getById(`address.label`),
      this.getById(`address.street`),
      this.getById(`address.number`),
      this.getById(`address.city`),
      this.getById(`address.postal_code`),
      this.getById(`address.county`),
      this.getById(`address.state`),
      this.getById(`address.country`),
    ].filter((v) => v);

    // There are three possible valid adddresses.
    // * If mapping contains address.lat and address.lng
    // * If mapping contains address.label
    // * If mapping contains address.street and address.number

    const samples: string[] = [];
    if (hasLat && hasLng) {
      this.data.forEach((row) => {
        samples.push(`${row[hasLat.column.index]}, ${row[hasLng.column.index]}`);
      });
    }
    if (hasLabel || (hasStreet && hasNumber)) {
      this.data.forEach((row) => {
        samples.push(tags.map((tag) => row[tag!.column.index]).join(`, `));
      });
    }
    return samples;
  }

  static validate(field: FieldDefinition, value: string) {
    if (value === undefined || value === null || value === ``) return true;
    if (field.type === `numerical`) {
      try {
        const numericValue = this.cast(field, value) as number;
        if (isNaN(numericValue)) return false;
        if (field.min !== undefined && numericValue < field.min) return false;
        if (field.max !== undefined && numericValue > field.max) return false;
      } catch (e) {
        return false;
      }
    }
    if (field.type === `boolean`) {
      return true;
    }
    if (field.type === `categorical`) {
      const categoricalValue = this.cast(field, value);
      if (value && !categoricalValue) return false;
    }
    if (field.type === `time_window`) {
      const timeWindowValue = this.cast(field, value) as number[] | number[][] | null;
      if (value && (!timeWindowValue || timeWindowValue.length === 0)) return false;
    }
    if (field.type === `phone`) {
      return Boolean(this.validatePhone(value));
    }
    return true;
  }

  private static validatePhone(value: string) {
    if (value === null) {
      return null;
    }
    try {
      const newPhone = `+${Number.parseInt(`${value}`.replace(/\D+/g, ``), 10)}`;
      const isValid = validatePhoneNumber(`${newPhone}`);
      if (isValid) {
        return `${newPhone}`;
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }

  static toStorage(mapping: FieldMapping) {
    return JSON.stringify(
      mapping.getItems().map((item) => ({
        field_id: item.field.id,
        column_name: item.column.name,
      }))
    );
  }

  static fromStorage(str: string, fields: FieldDefinition[], columns: ColumnDefinition[], data: any[][]) {
    const mapping = new FieldMapping([], data);
    const parsedData = JSON.parse(str);
    parsedData.forEach((item: any) => {
      const field = fields.find((field) => field.id === item.field_id);
      const column = columns.find((column) => column.name === item.column_name);
      if (!field || !column) return;
      mapping.add(field, column);
    });
    return mapping;
  }

  static isAddressField(field: FieldDefinition) {
    return field.id.startsWith(`address.`);
  }

  static sortFields(fields: FieldDefinition[], addressFields?: FieldDefinition[]) {
    if (!addressFields) return fields;
    const [firstAddress, ...other] = addressFields;
    return [firstAddress, ...fields, ...other];
  }
}
